import React, { useEffect, useState } from 'react';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { Session } from 'next-auth';
import Script from 'next/script';
import GreenlightScript from '!!raw-loader!../context/Optimizely/script.min';

import {
  hasOptedOut,
  CONSENT_COOKIE_KEY,
  readOnetrustSettings
} from '../utils/analytics/onetrust';

import { AuthProvider } from '../context/Auth';
import { UserProvider } from '../context/User';
import { PageDataProvider } from '../context/PageData';
import { OptimizelyProvider } from '../context/Optimizely';
import { SubscriptionsProvider } from '../context/Subscriptions';

import '@/styles/global.css';
import '@/fonts/style.css';
import '@/styles/normalize.css';
import '@/styles/bright-edge.css';
import '@/styles/outgrow.css';
import useSOCRadar from '~/utils/socRadar';
import { yieldToMain } from '~/utils/yieldToMain';

// Defer analytics/RUM init until browser idle (or after timeout). Standard pattern:
// https://nextjs.org/docs/app/api-reference/components/script#lazyonload
const DEFERRED_INIT_TIMEOUT_MS = 2000;

export default function App({
  Component,
  pageProps
}: AppProps<{ session: Session | null; cookies?: Record<string, string> }>) {
  useSOCRadar();

  // Send page hydrated event so the Coframe SDK fully initializes
  // (config sets waitForHydration: true)
  useEffect(() => {
    window.CFQ = window.CFQ || [];
    window.CFQ.push({ pageHydrated: true });
  }, []);

  const [shouldLoadOutbrain, setShouldLoadOutbrain] = useState<boolean>(() => {
    if (pageProps?.cookies?.[CONSENT_COOKIE_KEY]) {
      const settings = readOnetrustSettings(
        pageProps.cookies[CONSENT_COOKIE_KEY]
      );
      return settings?.C0004 !== false;
    }
    // Client-side fallback: synchronously read consent cookie to avoid
    // a 1-frame delay where Outbrain is absent then re-renders in.
    if (typeof document !== 'undefined') {
      return !hasOptedOut();
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Defer cookie read to a separate task to avoid adding to a long task (TBT)
    const timeoutId = setTimeout(() => {
      const optedOut = hasOptedOut();
      setShouldLoadOutbrain(!optedOut);

      if (optedOut) {
        const windowWithObApi = window as Window & { obApi?: unknown };
        if (windowWithObApi.obApi) {
          delete windowWithObApi.obApi;
        }

        const outbrainScripts = document.querySelectorAll(
          'script[src*="outbrain.com"], script[id="outbrain-bootstrap"]'
        );
        outbrainScripts.forEach((script) => script.remove());

        windowWithObApi.obApi = function () {
          return;
        };
      }
    }, 0);

    let cancelled = false;
    // Stagger analytics init with yieldToMain to avoid a single long task (TBT)
    const runWhenReady = async (): Promise<void> => {
      if (cancelled) return;
      const gtm = await import('../utils/analytics/gtmConsent');
      if (cancelled) return;
      gtm.initializeGTMConsent();
      await yieldToMain();
      if (cancelled) return;
      const rum = await import('../utils/datadogRum');
      if (cancelled) return;
      rum.initializeRum();
      await yieldToMain();
      if (cancelled) return;
      const mixpanel = await import('../utils/mixpanel');
      if (cancelled) return;
      mixpanel.initializeMixpanel();
    };
    const win = window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const useIdle = 'requestIdleCallback' in win;
    const id = useIdle
      ? win.requestIdleCallback!(runWhenReady, {
          timeout: DEFERRED_INIT_TIMEOUT_MS
        })
      : setTimeout(runWhenReady, 0);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      if (useIdle) win.cancelIdleCallback!(id as number);
      else clearTimeout(id as ReturnType<typeof setTimeout>);
    };
  }, []);

  return (
    <AuthProvider {...pageProps}>
      <UserProvider {...pageProps}>
        <PageDataProvider {...pageProps}>
          <OptimizelyProvider {...pageProps}>
            <SubscriptionsProvider>
              <>
                <Script id="optimizely-inline" strategy="afterInteractive">
                  {GreenlightScript}
                </Script>
                <Script
                  id="bright-edge"
                  src="https://cdn.bc0a.com/autopilot/f00000000295792/autopilot_sdk.js"
                  strategy="lazyOnload"
                />
                <Script
                  id="optimizely-cdn"
                  src="https://cdn-pci.optimizely.com/js/16064820140.js"
                  strategy="lazyOnload"
                />
                {shouldLoadOutbrain && (
                  <Script
                    id="outbrain-bootstrap"
                    strategy="lazyOnload"
                    data-obct
                  >
                    {`/** DO NOT MODIFY THIS CODE**/
!function(_window, _document) {
  var OB_ADV_ID = '000c41803d2459e753521bcbb44cfe44d5';
  if (_window.obApi) {
    var toArray = function(object) {
      return Object.prototype.toString.call(object) === '[object Array]' ? object : [object];
    };
    _window.obApi.marketerId = toArray(_window.obApi.marketerId).concat(toArray(OB_ADV_ID));
    return;
  }
  var api = _window.obApi = function() {
    api.dispatch ? api.dispatch.apply(api, arguments) : api.queue.push(arguments);
  };
  api.version = '1.1';
  api.loaded = true;
  api.marketerId = OB_ADV_ID;
  api.queue = [];
  var tag = _document.createElement('script');
  tag.async = true;
  tag.src = '//amplify.outbrain.com/cp/obtp.js';
  tag.type = 'text/javascript';
  var script = _document.getElementsByTagName('script')[0];
  script.parentNode.insertBefore(tag, script);
}(window, document);

obApi('track', 'PAGE_VIEW');`}
                  </Script>
                )}
                <Component {...pageProps} />
              </>
            </SubscriptionsProvider>
          </OptimizelyProvider>
        </PageDataProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (
    !['Next.js-render', 'Next.js-route-change-to-render'].includes(metric.name)
  ) {
    void import('../utils/datadogRum').then((m) =>
      m.addRumContext(metric.name, metric)
    );
  }
}
